import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ButtonToggleComponent,
  ButtonToggleOption,
  DialogShellComponent,
  FilledButtonComponent,
  OutlinedButtonComponent,
  PartnerSelectComponent,
  SelectFieldComponent,
  TextFieldComponent,
} from '@custom-components';
import { XEMEX_OID } from '@core/constants';
import {
  getUserActivePartner,
  InstallationGroupWithAccess,
  NewPartnership,
  PartnershipType,
} from '@core/models';
import { InstallationGroupService, PartnershipService } from '@core/services';
import { UserStore } from '@root/app/store/user.store';

type GuestMode = 'existing' | 'email';

/** Today + 1 year as a local `YYYY-MM-DD` string for the date input default. */
function oneYearFromToday(): string {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

@Component({
  selector: 'xemex-create-partnership-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatSelectModule,
    TranslateModule,
    DialogShellComponent,
    FilledButtonComponent,
    OutlinedButtonComponent,
    TextFieldComponent,
    SelectFieldComponent,
    PartnerSelectComponent,
    ButtonToggleComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './create-partnership-dialog.component.html',
})
export class CreatePartnershipDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<CreatePartnershipDialogComponent>);
  private readonly userStore = inject(UserStore);
  private readonly partnershipService = inject(PartnershipService);
  private readonly installationGroupService = inject(InstallationGroupService);
  private readonly translate = inject(TranslateService);

  /** Segments for the guest-mode toggle (label translated once; stable reference for the input). */
  readonly guestModeOptions: ButtonToggleOption[] = [
    { value: 'email', label: this.translate.instant('partnerships.create.guestByEmail') },
    { value: 'existing', label: this.translate.instant('partnerships.create.guestExisting') },
  ];

  readonly form = new FormGroup({
    guestMode: new FormControl<GuestMode>('email', { nonNullable: true }),
    guestPartnerOid: new FormControl<string | null>(null),
    guestEmail: new FormControl('', { nonNullable: true }),
    guestName: new FormControl('', { nonNullable: true }),
    partnershipType: new FormControl('', { nonNullable: true, validators: Validators.required }),
    reviewDueDate: new FormControl(oneYearFromToday(), { nonNullable: true }),
    installationGroupOids: new FormControl<string[]>([], { nonNullable: true }),
  });

  /** Owner partner picked by a Xemex admin; only used when {@link requiresPartnerSelection}. */
  readonly ownerControl = new FormControl<string | null>(null);
  readonly selectedPartnerOid = signal<string | null>(null);
  readonly partnerError = signal(false);
  /** Guest section failed validation (missing partner / email / name). */
  readonly guestError = signal(false);

  readonly partnershipTypes = signal<PartnershipType[]>([]);
  readonly installationGroups = signal<InstallationGroupWithAccess[]>([]);
  readonly isLoadingGroups = signal(false);

  /** True when no partner can be derived and the user must pick the owner (e.g. a Xemex admin). */
  readonly requiresPartnerSelection = computed(() => !this.autoPartnerOid());

  constructor() {
    this.partnershipService
      .getPartnershipTypes()
      .pipe(takeUntilDestroyed())
      .subscribe(res => this.partnershipTypes.set(res.partnershipTypes));

    this.ownerControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(partnerOid => {
      this.selectedPartnerOid.set(partnerOid || null);
      this.partnerError.set(false);
      // Installation groups are owner-scoped — reset and reload for the chosen owner.
      this.form.controls.installationGroupOids.setValue([]);
      this.loadInstallationGroups(this.resolveOwnerPartnerOid());
    });

    this.form.controls.guestMode.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.guestError.set(false));

    this.loadInstallationGroups(this.resolveOwnerPartnerOid());
  }

  /**
   * Partner derived from the logged-in user's active partner. Xemex admins carry the all-zero
   * placeholder OID ({@link XEMEX_OID}), normalized to `undefined` — they must pick an owner.
   */
  private autoPartnerOid(): string | undefined {
    const partnerOid = getUserActivePartner(this.userStore.user())?.partnerOid;
    return partnerOid && partnerOid !== XEMEX_OID ? partnerOid : undefined;
  }

  private resolveOwnerPartnerOid(): string | undefined {
    return this.selectedPartnerOid() ?? this.autoPartnerOid() ?? undefined;
  }

  /** Loads the owner-access installation groups for the given partner. */
  private loadInstallationGroups(partnerOid: string | undefined): void {
    if (!partnerOid) {
      this.installationGroups.set([]);
      this.isLoadingGroups.set(false);
      return;
    }
    // Set synchronously so the field renders immediately (with a loading state) on open,
    // instead of popping in once the request resolves.
    this.isLoadingGroups.set(true);
    this.installationGroupService
      .getInstallationGroups({ partnerOid, accessVia: 'owner', limit: 100, offset: 0 })
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: res => {
          this.installationGroups.set(res.installationGroups);
          this.isLoadingGroups.set(false);
        },
        error: () => {
          this.installationGroups.set([]);
          this.isLoadingGroups.set(false);
        },
      });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onCreate(): void {
    this.form.controls.partnershipType.markAsTouched();
    let invalid = this.form.controls.partnershipType.invalid;

    // Owner is required only when it can't be derived (Xemex admin).
    const ownerPartnerOid = this.requiresPartnerSelection() ? this.selectedPartnerOid() : null;
    if (this.requiresPartnerSelection() && !ownerPartnerOid) {
      this.partnerError.set(true);
      invalid = true;
    }

    const mode = this.form.controls.guestMode.value;
    const { guestPartnerOid, guestEmail, guestName } = this.form.getRawValue();
    if (mode === 'existing') {
      if (!guestPartnerOid) {
        this.guestError.set(true);
        invalid = true;
      }
    } else if (!guestEmail.trim() || !guestName.trim()) {
      this.guestError.set(true);
      invalid = true;
    }

    if (invalid) {
      return;
    }

    const { partnershipType, reviewDueDate, installationGroupOids } = this.form.getRawValue();
    const payload: NewPartnership = {
      partnershipType,
      installationGroupOids,
      ...(ownerPartnerOid ? { ownerPartnerOid } : {}),
      ...(reviewDueDate ? { reviewDueDate } : {}),
      ...(mode === 'existing'
        ? { guestPartnerOid: guestPartnerOid ?? undefined }
        : { guestEmail: guestEmail.trim(), guestName: guestName.trim() }),
    };
    this.dialogRef.close(payload);
  }
}
